import PropTypes from "prop-types"
import { createContext, useCallback, useEffect, useMemo, useRef, useState, useContext } from "react"
import useEmblaCarousel from "embla-carousel-react"
import { sendEvent } from "scripts/GoogleAnalytics"
import { CarContext } from "scenes/CarScene/CarContext"

export const SliderContext = createContext({})

const sliderOptions = { duration: 25, loop: true }

export function SliderContextProvider({ page, children }) {
  const { showMedia, setShowMedia } = useContext(CarContext)
  const [emblaRef, embla] = useEmblaCarousel(sliderOptions)
  const videoRef = useRef(null)
  const skipEvent = useRef(false)

  const videoIndex = useMemo(() => embla && embla.slideNodes().findIndex(node => node.id === "video"), [embla])
  const panoramaIndex = useMemo(() => embla && embla.slideNodes().findIndex(node => node.id === "panorama"), [embla])
  const [selectedMedia, setSelectedMedia] = useState(null)

  const slideToVideo = useCallback(() => {
    skipEvent.current = true
    if (embla) embla.scrollTo(videoIndex)
  }, [embla, videoIndex])

  useEffect(() => {
    if (embla) {
      if (videoRef.current) videoRef.current.pauseVideo()
    }
  }, [embla, showMedia])

  const onSlide = useRef()

  useEffect(() => {
    onSlide.current = (newIndex, oldIndex) => {
      if (oldIndex === newIndex) return

      if (newIndex === videoIndex) setSelectedMedia("video")
      else if (newIndex === panoramaIndex) setSelectedMedia("panorama")
      else if (selectedMedia) setSelectedMedia(null)

      if (videoRef.current) videoRef.current.pauseVideo()

      if (skipEvent.current) skipEvent.current = false
      else
        sendEvent("Photo", {
          event_category: page,
          event_label: `Slide ${oldIndex < newIndex ? "ahead" : "back"}`
        })
    }
  }, [videoIndex, panoramaIndex, selectedMedia, page, setShowMedia])

  useEffect(() => {
    if (embla) embla.reInit()
  }, [embla])

  useEffect(() => {
    if (!embla) return undefined

    const handleSelect = () => onSlide.current(embla.selectedScrollSnap(), embla.previousScrollSnap())
    embla.on("select", handleSelect)

    return () => {
      embla.off("select", handleSelect)
    }
  }, [embla])

  const value = useMemo(
    () => ({
      videoRef,
      embla,
      emblaRef,
      slideToVideo,
      selectedMedia
    }),
    [embla, emblaRef, slideToVideo, selectedMedia]
  )

  return <SliderContext.Provider value={value}>{children}</SliderContext.Provider>
}

SliderContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  page: PropTypes.string.isRequired
}
